import {
  JOINT_OVERLAP_INCHES,
  IPipeConnection,
  IStackComponent,
  isComponentType,
  getEffectiveHeightInches,
} from "../../../../types/manhole.types";

/**
 * Derived pipe location within the component stack.
 * Used by CalculatedDataSummary, SubmittalDrawing, and store getters.
 */
export interface DerivedPipeLocation {
  componentLocation: "base" | "riser" | "cone" | "unknown";
  componentIndex: number;
  holePositionInches: number;
  componentDescription: string;
}

/**
 * Derive component location and hole position from invert elevation and component stack.
 * Per Clay's spec: these values are calculated, not user-entered.
 *
 * @param pipe - The pipe connection to locate
 * @param components - The component stack (sorted bottom-up internally)
 * @param rimElevation - Rim elevation in feet (unused but kept for API compatibility)
 * @param lowestInvert - Lowest pipe invert elevation in feet
 */
export function derivePipeLocation(
  pipe: IPipeConnection,
  components: IStackComponent[],
  rimElevation: number,
  lowestInvert: number,
): DerivedPipeLocation {
  if (components.length === 0) {
    return {
      componentLocation: "base",
      componentIndex: 0,
      holePositionInches: 6,
      componentDescription: "No components",
    };
  }

  // Build component stack from bottom up
  // The base extends BELOW the lowest pipe invert by default 6 inches
  // This ensures hole position calculations match the default 6" assumption
  const DEFAULT_BASE_BELOW_INVERT_FT = 0.5; // 6 inches = 0.5 feet

  const sortedComponents = [...components].sort(
    (a, b) => (a.positionInStack || 0) - (b.positionInStack || 0),
  );

  // Calculate cumulative heights from bottom
  // Base bottom = lowest invert minus the distance the base extends below the pipe
  let currentBottomElevation = lowestInvert - DEFAULT_BASE_BELOW_INVERT_FT;
  const componentElevations: Array<{
    component: IStackComponent;
    bottomElevation: number;
    topElevation: number;
    type: "base" | "riser" | "cone" | "other";
  }> = [];

  sortedComponents.forEach((comp, index) => {
    const heightFeet = getEffectiveHeightInches(comp) / 12;
    // Add joint overlap for all components except the first
    const jointFeet = index > 0 ? JOINT_OVERLAP_INCHES / 12 : 0;
    const topElevation = currentBottomElevation + heightFeet + jointFeet;

    // Determine component type
    let type: "base" | "riser" | "cone" | "other" = "other";
    if (isComponentType(comp.product.type, "BASE")) {
      type = "base";
    } else if (
      isComponentType(comp.product.type, "RISER") ||
      isComponentType(comp.product.type, "SECTION")
    ) {
      type = "riser";
    } else if (isComponentType(comp.product.type, "CONE")) {
      type = "cone";
    }

    componentElevations.push({
      component: comp,
      bottomElevation: currentBottomElevation,
      topElevation,
      type,
    });

    currentBottomElevation = topElevation;
  });

  // Find which component the pipe invert falls within
  const pipeInvert = pipe.invertElevation;

  for (let i = 0; i < componentElevations.length; i++) {
    const compInfo = componentElevations[i];
    if (
      pipeInvert >= compInfo.bottomElevation &&
      pipeInvert <= compInfo.topElevation
    ) {
      // Pipe is in this component
      const distanceFromBottom = (pipeInvert - compInfo.bottomElevation) * 12; // Convert to inches

      return {
        componentLocation: compInfo.type === "other" ? "riser" : compInfo.type,
        componentIndex: i,
        holePositionInches: Math.round(distanceFromBottom * 10) / 10, // Round to 0.1"
        componentDescription:
          compInfo.component.product.description || compInfo.type,
      };
    }
  }

  // If not found in any component, it's likely at or below the base
  if (componentElevations.length > 0) {
    const firstComp = componentElevations[0];
    if (pipeInvert < firstComp.bottomElevation) {
      // Below the stack - put in base at calculated position
      return {
        componentLocation: firstComp.type === "other" ? "base" : firstComp.type,
        componentIndex: 0,
        holePositionInches: Math.max(
          6,
          (pipeInvert -
            firstComp.bottomElevation +
            getEffectiveHeightInches(firstComp.component) / 12) *
            12,
        ),
        componentDescription: firstComp.component.product.description || "Base",
      };
    }
  }

  // Default fallback
  return {
    componentLocation: "base",
    componentIndex: 0,
    holePositionInches: 6,
    componentDescription: "Unknown",
  };
}
